import { ref } from 'vue';
import { useApiMutation, useToast } from '@lumi/app-kit';
import { timeApi } from '@/utils/api';
import {
  activeActivities,
  bookableProjects,
  runningProjectOf,
  type ClockActivity,
  type ClockProject,
  type RunningProject,
} from '@/utils/clockProjects';

// The home Stempeluhr's project choice (see utils/clockProjects.ts): the reads
// it needs and the two timer writes Zeit's Start screen makes for the same
// choice (time-ui DashboardView pickProject / pickNone / changeActivity).
export function useClockProjects() {
  const toast = useToast();
  const projects = ref<ClockProject[]>([]);
  const activities = ref<ClockActivity[]>([]);
  const running = ref<RunningProject | null>(null);

  // What she may book on. A read that fails (no `project.view`, Time briefly
  // away) leaves an empty list, and an empty list offers no choice at all: the
  // tile is then the plain clock it always was.
  async function loadChoices() {
    const [projectsRes, activitiesRes] = await Promise.allSettled([
      timeApi.get('/projects', { suppressGlobalError: true }),
      timeApi.get('/activities', { suppressGlobalError: true }),
    ]);
    projects.value = projectsRes.status === 'fulfilled' ? bookableProjects(projectsRes.value.data) : [];
    activities.value = activitiesRes.status === 'fulfilled' ? activeActivities(activitiesRes.value.data) : [];
  }

  // Which project runs inside the open block. A failed read shows "Ohne Projekt"
  // -- the next write or reload says the truth.
  async function loadRunning() {
    try {
      const { data } = await timeApi.get('/timer', { suppressGlobalError: true });
      running.value = runningProjectOf(data);
    } catch {
      running.value = null;
    }
  }

  // inlineEdit: the tile repaints with the project, which is the confirmation.
  // CONSUMER (`errorChannel: 'handled'`): `pick` / `pickNone` below report the
  // server's own reason (a handed-in week, a closed project) as a toast and
  // re-read the timer, so the tile never shows a project that is not running.
  const { pending: startBusy, mutate: startRequest } = useApiMutation<void, [{ projectId: string; activityId: string | null }]>({
    tier: 'inlineEdit',
    errorChannel: 'handled',
    run: async ({ projectId, activityId }) => {
      // A start while another project runs is a SWITCH on the server: the
      // running one is booked up to now and the block stays open.
      await timeApi.post('/timer/start', activityId ? { projectId, activityId } : { projectId }, { suppressGlobalError: true });
    },
  });

  // Only ever called while a project timer runs: a stop with none running is
  // a clock-out (the timer contract, time-api work-time-rules.ts).
  // CONSUMER (`errorChannel: 'handled'`): `pickNone` below shows the server's
  // reason as a toast and re-reads the timer, so the tile keeps the project
  // that is still running.
  const { pending: stopBusy, mutate: stopRequest } = useApiMutation<void, []>({
    tier: 'inlineEdit',
    errorChannel: 'handled',
    run: async () => {
      await timeApi.post('/timer/stop', {}, { suppressGlobalError: true });
    },
  });

  async function pick(projectId: string, activityId: string | null): Promise<boolean> {
    if (startBusy.value || stopBusy.value) return false;
    const outcome = await startRequest({ projectId, activityId });
    if (!outcome.ok) toast.error(outcome.formError);
    await loadRunning();
    return outcome.ok;
  }

  async function pickNone(): Promise<boolean> {
    if (startBusy.value || stopBusy.value) return false;
    if (!running.value) return true;
    const outcome = await stopRequest();
    if (!outcome.ok) toast.error(outcome.formError);
    await loadRunning();
    return outcome.ok;
  }

  function clear() {
    running.value = null;
  }

  return { projects, activities, running, startBusy, stopBusy, loadChoices, loadRunning, pick, pickNone, clear };
}
